import {
  Component, OnInit, OnDestroy, HostListener, HostBinding, ViewChild, ElementRef, AfterViewChecked, Input,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AssistantService, AssistantReply, ChatScope, ChatTurn, HistoryPage, NotificationItem } from 'src/@core/services/assistant/assistant.service';
import { AryaChatService } from 'src/@core/services/assistant/arya-chat.service';
import { environment } from 'src/environments/environment';

/**
 * Converts a markdown-ish assistant reply to sanitized HTML.
 *
 * Safety approach: escape raw HTML characters FIRST, then apply markdown
 * substitutions on the escaped string — so model output can never inject
 * arbitrary tags, only the patterns we explicitly allow.
 */
function markdownToHtml(raw: string): string {
  // Empty / whitespace-only reply → render nothing (avoid stray empty <ul></ul>)
  if (!raw || !raw.trim()) {
    return '';
  }

  // 1. Escape HTML special chars so model output is neutralized
  const escaped = raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  // 2. Split into blocks on blank lines (paragraph breaks)
  const blocks = escaped.split(/\n{2,}/);

  const renderedBlocks = blocks.map(block => {
    // Skip blank/whitespace-only blocks (avoid stray empty <ul></ul> / <p></p>)
    if (block.trim() === '') {
      return '';
    }

    const lines = block.split('\n');

    // Detect bullet list block (lines starting with - or •) — needs ≥1 real bullet
    const isBullet = lines.some(l => /^\s*[-•]\s/.test(l)) &&
      lines.every(l => /^\s*[-•]\s/.test(l) || l.trim() === '');
    if (isBullet) {
      const items = lines
        .filter(l => l.trim() !== '')
        .map(l => `<li>${applyInline(l.replace(/^\s*[-•]\s*/, ''))}</li>`)
        .join('');
      return `<ul>${items}</ul>`;
    }

    // Detect numbered list block (lines starting with N.) — needs ≥1 real item
    const isNumbered = lines.some(l => /^\s*\d+\.\s/.test(l)) &&
      lines.every(l => /^\s*\d+\.\s/.test(l) || l.trim() === '');
    if (isNumbered) {
      const items = lines
        .filter(l => l.trim() !== '')
        .map(l => `<li>${applyInline(l.replace(/^\s*\d+\.\s*/, ''))}</li>`)
        .join('');
      return `<ol>${items}</ol>`;
    }

    // Plain paragraph — join lines with <br> for single newlines
    const content = lines.map(l => applyInline(l)).join('<br>');
    return `<p>${content}</p>`;
  });

  return renderedBlocks.join('');
}

/** Apply inline markdown patterns (bold, inline code) to an already-escaped line. */
function applyInline(line: string): string {
  return line
    // **bold** (already HTML-escaped, so no < > risk)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // *italic*
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // `inline code`
    .replace(/`([^`]+)`/g, '<code>$1</code>');
}

interface ChatMsg {
  text: string;
  who: 'me' | 'bot';
  mode?: string;
  msgType?: string;   // visible message-type chip (Report / Data / Answer / Support)
  download?: { filename: string; content?: string; base64?: string; mime?: string; encoding?: string };
  displayText?: string;
  /** Pre-rendered safe HTML for bot messages (set once streaming completes). */
  renderedHtml?: SafeHtml;
  streaming?: boolean;
  notificationKind?: string;
  /** epoch ms when the message was sent/received (WhatsApp-style timestamp). */
  ts?: number;
  /** Scope of the answer returned by the backend (account-wide or campaign). */
  scope?: ChatScope;
  /** request_id from the backend for in-flight cancel. */
  requestId?: string;
}

interface Campaign {
  id: string;
  label: string;
}

/** Max conversation turns kept in memory (for the on-screen feed only). */
const HISTORY_MAX = 30;
// NOTE: history is no longer SENT to the backend (stateless requests — see send()).
// HISTORY_SENT was removed; per-user memory will be server-side later.
/** How many prior turns to load per history page (initial open + each scroll-up fetch). */
const HISTORY_PAGE = 40;
/** Scroll distance from the top (px) that triggers loading older history. */
const LOAD_MORE_THRESHOLD = 60;
/** Route that renders Arya full-page inside the app content area. */
const ARYA_ROUTE = '/arya';

/**
 * Arya in-app chat — replaces the Cloudflare-injected widget.
 * Mounted globally in main-layout; renders a floating FAB + panel.
 * Full-screen content-area mode is activated by the sidebar "Arya" nav item
 * (the nav item calls openFull() via the AryaChatService event bus).
 */
@Component({
  selector: 'codemiro-arya-chat',
  templateUrl: './arya-chat.component.html',
  styleUrls: ['./arya-chat.component.scss'],
})
export class AryaChatComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('feedEl', { static: false }) feedEl: ElementRef<HTMLElement>;
  @ViewChild('chatInput', { static: false }) chatInputEl: ElementRef<HTMLTextAreaElement>;

  /**
   * Page mode: this instance is the full-page /arya route host (rendered in the
   * app content area). It mounts the chat inline (no FAB / no floating panel)
   * and is always "open". The global instance keeps pageMode = false and shows
   * the floating FAB + docked panel on other routes.
   */
  @Input() pageMode = false;

  /** Applies full-page host styling when this instance is the /arya route host. */
  @HostBinding('class.arya-host-page') get hostPage(): boolean { return this.pageMode; }

  open = false;
  /** Setup drawer (Channels / Integrations / Skills / Memory / Credit Usage / Settings). */
  setupOpen = false;
  /** Full-screen (content-area) mode — hides FAB, panel fills mat-sidenav-content. */
  fullMode = false;
  available = true;
  /** True while the /arya route is active — used by the global instance to hide its FAB/panel. */
  onAryaRoute = false;
  /** True once history has been loaded for this open session (prevents re-loading on every render). */
  private historyLoaded = false;

  /** Number of history turns loaded so far (becomes the offset for the next older page). */
  private historyOffset = 0;
  /** Whether older history turns exist on the server (drives the scroll-up fetch + indicator). */
  historyHasMore = false;
  /** Reentrancy guard: true while an older-history page is being fetched/prepended. */
  loadingOlder = false;
  /** True once the user has scrolled back to the very first turn (subtle "beginning" marker). */
  reachedBeginning = false;

  inFlight = false;
  /** Rotating status shown beside the typing dots ('Thinking…', 'Reading your data…'). */
  typingStatus = 'Thinking…';
  /** True once a streaming bot bubble is visible — hides the separate typing
   *  indicator so we never show an empty bubble + dots at the same time. */
  streamingShown = false;
  private typingTimer?: ReturnType<typeof setInterval>;
  draft = '';
  messages: ChatMsg[] = [];
  quickReplies: Campaign[] = [];

  showPicker = false;
  pickerCampaigns: Campaign[] = [];

  greeted = false;
  ownerName = '';
  history: ChatTurn[] = [];

  selectedProjectId: string | null = null;
  selectedProjectName: string | null = null;

  /** Scope chip: the scope of the last bot answer (null = none to show). */
  lastScope: ChatScope | null = null;
  /** When the user corrects scope, send next prompt with this project id. */
  scopeOverrideProjectId: string | null = null;
  scopeOverrideProjectName: string | null = null;
  /** Show the scope-correction selector (search input). */
  showScopeSelector = false;
  scopeSearch = '';

  /** request_id of the currently in-flight streaming request (for stop button). */
  private inflightRequestId: string | null = null;
  /** True while the stop (cancel) request is being sent to the backend. */
  stopping = false;
  /** Shown after a stop — 'Stopped.' note. */
  stoppedNote = '';

  private shouldScrollFeed = false;
  private subs: Subscription[] = [];
  private typewriterIntervals = new Map<ChatMsg, ReturnType<typeof setInterval>>();
  private fetchingNotifications = false;
  destroyed = false;

  constructor(
    public assistant: AssistantService,
    private aryaChatService: AryaChatService,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) {
    this.ownerName = this.assistant.getOwnerName();
    this.checkHealth();
    this.onAryaRoute = this.isAryaUrl(this.router.url);
    // Track route changes: keep onAryaRoute in sync, and (docked instance only)
    // close any open docked panel when the user navigates away.
    this.subs.push(
      this.router.events
        .pipe(filter(e => e instanceof NavigationEnd))
        .subscribe((e) => {
          this.onAryaRoute = this.isAryaUrl((e as NavigationEnd).urlAfterRedirects || (e as NavigationEnd).url);
          if (!this.pageMode && this.open) { this.close(); }
        }),
      // Backwards-compat: legacy sidebar nav (function route) still emits openFull$.
      // Navigate to the dedicated full-page route instead of opening a panel.
      this.aryaChatService.openFull$.subscribe(() => this.router.navigateByUrl(ARYA_ROUTE)),
    );
  }

  ngOnInit() {
    if (this.pageMode) {
      // Full-page route host: mount the conversation immediately. Polling stays
      // owned by the always-mounted global instance, so we don't start/stop it
      // here (the page host is created/destroyed on every /arya nav).
      this.bootstrapConversation();
    }
  }

  private isAryaUrl(url: string): boolean {
    if (!url) { return false; }
    const path = url.split(/[?#]/)[0];
    return path === ARYA_ROUTE || path.startsWith(ARYA_ROUTE + '/');
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.subs.forEach(s => s.unsubscribe());
    // Clear all typewriter intervals
    this.typewriterIntervals.forEach((handle) => clearInterval(handle));
    this.typewriterIntervals.clear();
  }

  ngAfterViewChecked() {
    if (this.shouldScrollFeed) {
      this.shouldScrollFeed = false;
      const el = this.feedEl && this.feedEl.nativeElement;
      if (el) { el.scrollTop = el.scrollHeight; }
    }
  }

  // ── Health check ──────────────────────────────────────────────────────────

  private checkHealth() {
    fetch(`${environment.assistantApiUrl}/health`, { cache: 'no-store' })
      .then(r => { if (!r.ok) { throw new Error('down'); } return r.json(); })
      .then((j: any) => { if (j && j.enabled === false) { throw new Error('disabled'); } })
      .catch(() => { this.available = false; });
  }

  // ── Campaign helpers ──────────────────────────────────────────────────────

  get currentProjectId(): string | null {
    if (this.selectedProjectId) { return this.selectedProjectId; }
    try {
      const m = location.pathname.match(/projects?\/([a-zA-Z0-9_-]{7,})/);
      if (m) { return m[1]; }
      const q = new URLSearchParams(location.search);
      return q.get('projectId') || q.get('project_id') || null;
    } catch (_) { return null; }
  }

  setSelectedProject(id: string, name: string) {
    this.selectedProjectId = id;
    this.selectedProjectName = name || null;
  }

  clearSelectedProject() {
    this.selectedProjectId = null;
    this.selectedProjectName = null;
    this.send('pick_campaign', true, 'Change campaign');
  }

  // ── Scope chip helpers ────────────────────────────────────────────────────

  clearScopeChip(): void {
    this.lastScope = null;
    this.scopeOverrideProjectId = null;
    this.scopeOverrideProjectName = null;
    this.showScopeSelector = false;
    this.scopeSearch = '';
  }

  toggleScopeSelector(): void {
    this.showScopeSelector = !this.showScopeSelector;
    this.scopeSearch = '';
  }

  setScopeOverride(id: string, name: string): void {
    this.scopeOverrideProjectId = id;
    this.scopeOverrideProjectName = name;
    this.showScopeSelector = false;
    this.scopeSearch = '';
  }

  get scopeLabel(): string {
    if (!this.lastScope) { return ''; }
    // Normalise across backend field names: `level` (current) and `kind` (legacy).
    const level = this.lastScope.level || this.lastScope.kind;
    const pid = this.lastScope.project_id;
    // Account-wide (or no project at all) → never a UUID, always a friendly label.
    if (level === 'account' || !pid) { return 'Account-wide'; }
    // Campaign: prefer the backend-supplied name; else resolve from the local list.
    const backendName = this.lastScope.project_name;
    if (backendName) { return backendName; }
    const found = this.pickerCampaigns.find(c => c.id === pid);
    if (found && found.label) { return found.label; }
    // We have only an unresolvable id — show a friendly placeholder, NEVER the UUID.
    return 'Selected project';
  }

  /** Campaigns matching the scope-selector type-search box. */
  get filteredScopeCampaigns(): Campaign[] {
    const q = (this.scopeSearch || '').trim().toLowerCase();
    if (!q) { return this.pickerCampaigns; }
    return this.pickerCampaigns.filter(c => (c.label || '').toLowerCase().includes(q));
  }

  // ── Stop button helpers ───────────────────────────────────────────────────

  /** Abort the in-flight request client-side and call the cancel endpoint. */
  private inflightReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

  async stopChat(): Promise<void> {
    if (!this.inFlight || this.stopping) { return; }
    this.stopping = true;
    // 1. Abort client-side reader if we have one.
    if (this.inflightReader) {
      try { this.inflightReader.cancel(); } catch (_) {}
      this.inflightReader = null;
    }
    // 2. Tell backend to cancel its background processing.
    if (this.inflightRequestId) {
      try {
        await this.assistant.cancelChat(this.inflightRequestId);
      } catch (_) {}
      this.inflightRequestId = null;
    }
    this.inFlight = false;
    this.streamingShown = false;
    this.stopTypingStatus();
    this.stoppedNote = 'Stopped.';
    this.stopping = false;
    // Clear the stopped note after 3s.
    setTimeout(() => { this.stoppedNote = ''; }, 3000);
    setTimeout(() => {
      if (this.chatInputEl && this.chatInputEl.nativeElement) {
        this.chatInputEl.nativeElement.focus();
      }
    }, 0);
  }

  // ── Panel open/close ──────────────────────────────────────────────────────

  toggle() {
    if (!this.available) { return; }
    if (this.open) { this.close(); } else { this.openPanel(); }
  }

  openPanel() {
    this.open = true;
    setTimeout(() => {
      if (this.chatInputEl && this.chatInputEl.nativeElement) {
        this.chatInputEl.nativeElement.focus();
      }
    }, 50);
    this.bootstrapConversation();
    if (!this.inFlight) {
      this.onOpenFetch();
    }
  }

  /**
   * Loads prior conversation history (oldest→newest) and renders it instantly
   * BEFORE greeting. If history exists, the greeting is suppressed (we treat
   * the session as continuing). If history is empty / fails, show the greeting
   * as a fresh conversation. Runs once per open session.
   */
  private async bootstrapConversation(): Promise<void> {
    if (this.historyLoaded) { return; }
    this.historyLoaded = true;

    let page: HistoryPage;
    try {
      page = await this.assistant.history(HISTORY_PAGE, 0);
    } catch (_) {
      page = { history: [], count: 0, has_more: false, offset: 0, total: 0 };
    }
    if (this.destroyed) { return; }

    const prior = page.history;

    if (prior.length) {
      for (const t of prior) {
        const text = String(t.text || '');
        this.messages.push(this.historyTurnToMsg(t.role, text, typeof t.ts === 'string' ? Date.parse(t.ts) : t.ts));
        this.addHistory(t.role, text);
      }
      // Track how far back we've loaded + whether older turns remain.
      this.historyOffset = prior.length;
      this.historyHasMore = page.has_more;
      // History present → don't append today's greeting (continuing session).
      this.greeted = true;
      this.shouldScrollFeed = true;
    } else if (!this.greeted) {
      // Empty / failed history → greet as a fresh conversation today.
      this.greeted = true;
      this.send('hi', true /* no visible user bubble for the greeting */);
    }
  }

  /**
   * Maps a persisted history turn to a render-ready ChatMsg. Assistant turns
   * are pre-rendered through the SAME escape-first markdownToHtml path (XSS-safe,
   * no typewriter for history); user turns stay plain text (never innerHTML).
   */
  private historyTurnToMsg(role: 'user' | 'assistant', text: string, ts?: number): ChatMsg {
    if (role === 'assistant') {
      return {
        text,
        who: 'bot',
        ts,
        renderedHtml: this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(text)),
      };
    }
    return { text, who: 'me', ts };
  }

  /**
   * Scroll handler for the message feed: when the user scrolls near the TOP and
   * older history exists, fetch + prepend the next older page. Bound from the
   * template via (scroll).
   */
  onFeedScroll(): void {
    const el = this.feedEl && this.feedEl.nativeElement;
    if (!el) { return; }
    if (el.scrollTop < LOAD_MORE_THRESHOLD && this.historyHasMore && !this.loadingOlder) {
      this.loadOlderHistory();
    }
  }

  /**
   * Fetches the next older page of history and PREPENDS it above the current
   * messages (oldest→newest order preserved), preserving the user's scroll
   * position so the view doesn't jump.
   *
   * Guarded by `loadingOlder` against concurrent/duplicate loads.
   */
  private async loadOlderHistory(): Promise<void> {
    if (this.loadingOlder || !this.historyHasMore) { return; }
    this.loadingOlder = true;

    const el = this.feedEl && this.feedEl.nativeElement;
    const prevScrollHeight = el ? el.scrollHeight : 0;
    const prevScrollTop = el ? el.scrollTop : 0;

    let page: HistoryPage;
    try {
      page = await this.assistant.history(HISTORY_PAGE, this.historyOffset);
    } catch (_) {
      page = { history: [], count: 0, has_more: this.historyHasMore, offset: this.historyOffset, total: 0 };
    }
    if (this.destroyed) { this.loadingOlder = false; return; }

    const older = page.history;
    if (older.length) {
      // Build the prepend batch (oldest→newest), then splice ABOVE current msgs.
      const batch: ChatMsg[] = older.map(t => this.historyTurnToMsg(t.role, String(t.text || '')));
      this.messages = [...batch, ...this.messages];
      // `this.history` only feeds the last HISTORY_SENT turns to the backend, so
      // prepended older turns never affect a send. Cap total length to keep the
      // in-memory context bounded (keep the newest HISTORY_MAX turns).
      const olderTurns: ChatTurn[] = older.map(t => ({ role: t.role, text: String(t.text || '') }));
      this.history = [...olderTurns, ...this.history].slice(-HISTORY_MAX);

      this.historyOffset += page.count || older.length;
      this.historyHasMore = page.has_more;

      // Preserve scroll position: after the DOM grows, push scrollTop down by the
      // height delta so the previously-visible turns stay put (no jump).
      setTimeout(() => {
        const node = this.feedEl && this.feedEl.nativeElement;
        if (node) {
          node.scrollTop = prevScrollTop + (node.scrollHeight - prevScrollHeight);
        }
        this.loadingOlder = false;
      }, 0);
    } else {
      this.historyHasMore = page.has_more;
      if (!this.historyHasMore) { this.reachedBeginning = true; }
      this.loadingOlder = false;
    }

    if (!this.historyHasMore) { this.reachedBeginning = true; }
  }

  close() {
    if (this.pageMode) { return; }
    this.open = false;
    if (this.fullMode) { this.exitFull(); }
  }

  /** Legacy entry point (kept for compat) — now routes to the full-page /arya view. */
  openFull() {
    this.router.navigateByUrl(ARYA_ROUTE);
  }

  exitFull() {
    this.fullMode = false;
    this.open = false;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    // The Setup drawer owns Escape while open (handled in its own listener);
    // only close the docked chat panel when Setup is closed.
    if (!this.setupOpen && !this.pageMode && this.open) { this.close(); }
  }

  // ── Setup drawer ──────────────────────────────────────────────────────────

  openSetup() {
    if (!this.available) { return; }
    this.setupOpen = true;
  }

  closeSetup() {
    this.setupOpen = false;
  }

  /**
   * The Setup drawer asks Arya to do something.
   * - If prompt starts with "__PREFILL__": pre-fill the input for the user to review + send manually.
   * - Otherwise: send immediately through the normal chat flow.
   */
  onSetupAsk(prompt: string) {
    this.setupOpen = false;
    if (!this.pageMode && !this.open) { this.openPanel(); }
    if (prompt.startsWith('__PREFILL__')) {
      // Pre-fill only — user must review and press Send themselves.
      this.draft = prompt.slice('__PREFILL__'.length);
      setTimeout(() => {
        if (this.chatInputEl && this.chatInputEl.nativeElement) {
          this.chatInputEl.nativeElement.focus();
          this.autoGrow();
        }
      }, 50);
    } else {
      this.draft = prompt;
      setTimeout(() => this.submitDraft(), 0);
    }
  }

  // ── Typewriter ────────────────────────────────────────────────────────────

  /**
   * Reveals `fullText` per-word (token/whitespace boundary) into a single
   * stable [innerHTML] element — no plain→HTML swap at the end.
   *
   * Bot messages: each step calls markdownToHtml(revealedSoFar) and stores
   * the result in msg.renderedHtml, which the template always renders via
   * [innerHTML].  An unclosed "**" stays as literal ** until its pair
   * arrives, then renders bold — the escape-first path in markdownToHtml
   * handles this correctly.
   *
   * Non-bot messages fall back to plain-text displayText (unchanged).
   */
  typewriterReveal(msg: ChatMsg, fullText: string, onComplete?: () => void): void {
    // Clear any existing interval for this message (prevents duplicate reveals)
    const ex = this.typewriterIntervals.get(msg);
    if (ex) { clearInterval(ex); }

    msg.streaming = true;
    msg.displayText = '';

    if (msg.who === 'bot') {
      // Seed with empty rendered HTML so the stable div is present from step 1
      msg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml('');
    } else {
      msg.renderedHtml = undefined;
    }

    // Split on whitespace boundaries — each token is a word or a run of spaces/newlines.
    // This preserves spacing exactly when reassembled.
    const tokens = fullText.match(/\S+|\s+/g) || [];
    let tokenIdx = 0;

    const handle = setInterval(() => {
      tokenIdx++;
      const revealed = tokens.slice(0, tokenIdx).join('');

      if (msg.who === 'bot') {
        // Always render through the same escape-first markdown path
        msg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(revealed));
      } else {
        msg.displayText = revealed;
      }

      this.shouldScrollFeed = true;

      if (tokenIdx >= tokens.length) {
        clearInterval(handle);
        this.typewriterIntervals.delete(msg);
        msg.streaming = false;
        msg.displayText = undefined;
        // Ensure the final render uses the full text (handles edge case where
        // the last token is whitespace-only and markdownToHtml trims it)
        if (msg.who === 'bot') {
          msg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(fullText));
        }
        if (onComplete) { onComplete(); }
      }
    }, 50); // ~50 ms per word — smooth and readable, far fewer reflows than per-char

    this.typewriterIntervals.set(msg, handle);
  }

  skipTypewriter(msg: ChatMsg): void {
    if (msg.streaming) {
      const handle = this.typewriterIntervals.get(msg);
      if (handle !== undefined) {
        clearInterval(handle);
        this.typewriterIntervals.delete(msg);
      }
      msg.streaming = false;
      msg.displayText = undefined;
      // Immediately render as markdown HTML when skipped
      if (msg.who === 'bot') {
        msg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(msg.text));
      }
    }
  }

  /**
   * Finish ALL in-flight client-side typewriter reveals immediately. Called
   * before sending a new message so a partially-revealed previous answer snaps
   * to its full text instead of continuing to animate behind the new turn.
   */
  private flushAllTypewriters(): void {
    // Snapshot keys first — skipTypewriter mutates the map while iterating.
    Array.from(this.typewriterIntervals.keys()).forEach(msg => this.skipTypewriter(msg));
  }

  // ── Notification fetch on open ────────────────────────────────────────────

  async onOpenFetch(): Promise<void> {
    // Concurrency guard: prevent duplicate notifications on rapid double-open
    if (this.fetchingNotifications) { return; }
    this.fetchingNotifications = true;
    try {
      const items = await this.assistant.fetchAndAckNotifications(false);
      if (this.destroyed) { return; }
      const unread = items.filter(item => !item.read);
      if (unread.length === 0) { return; }

      // Push all notification messages first, then ack only after each has
      // fully revealed (so an interrupted reveal does NOT ack — server-side
      // notifications are idempotent and will re-show on next open).
      let revealedCount = 0;
      const checkAllRevealed = () => {
        revealedCount++;
        if (revealedCount === unread.length && !this.destroyed) {
          this.assistant.ackNotifications();
        }
      };

      for (const item of unread) {
        const botMsg: ChatMsg = { text: item.text, who: 'bot', notificationKind: item.kind, ts: Date.now() };
        this.messages.push(botMsg);
        this.typewriterReveal(botMsg, item.text, checkAllRevealed);
      }
    } finally {
      this.fetchingNotifications = false;
    }
  }

  // ── Conversation ──────────────────────────────────────────────────────────

  chip(c: Campaign) {
    if (c.id && c.id.startsWith('project:')) {
      const pid = c.id.slice(8);
      this.setSelectedProject(pid, c.label);
      this.pushMsg(`📁 ${c.label}`, 'me');
      this.addHistory('user', `Selected campaign: ${c.label}`);
      this.send('menu', true);
      return;
    }
    this.send(c.id, false, c.label);
  }

  pickerChange(event: Event) {
    const sel = event.target as HTMLSelectElement;
    const id = sel.value;
    if (!id) { return; }
    const opt = sel.options[sel.selectedIndex];
    const name = (opt && opt.text) || '';
    this.setSelectedProject(id, name);
    this.pushMsg(`📁 ${name}`, 'me');
    this.addHistory('user', `Selected campaign: ${name}`);
    this.showPicker = false;
    this.pickerCampaigns = [];
    this.send('menu', true);
  }

  submitDraft() {
    const m = (this.draft || '').trim();
    if (m) {
      this.draft = '';
      this.send(m);
      this.resetInputHeight();
    }
  }

  /** Enter sends; Shift+Enter inserts a newline (ChatGPT-style). */
  onInputKeydown(e: KeyboardEvent): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.submitDraft();
    }
  }

  /** Grow the textarea with its content, up to ~4 lines, then scroll. */
  autoGrow(): void {
    const el = this.chatInputEl && this.chatInputEl.nativeElement;
    if (!el) { return; }
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }

  /** Collapse the textarea back to a single row (after send / clear). */
  private resetInputHeight(): void {
    const el = this.chatInputEl && this.chatInputEl.nativeElement;
    if (el) { el.style.height = ''; }
  }

  /** Derive a friendly message-type chip from the reply. '' = no chip. */
  private deriveMsgType(r: any): string {
    if (!r) { return ''; }
    if (r.download) { return 'Report'; }
    const tool = (r.data && r.data[0] && r.data[0].tool) || '';
    if (tool === 'create_ticket') { return 'Support'; }
    if (r.mode === 'deterministic' || tool) { return 'Data'; }
    if (r.mode === 'llm') { return 'Answer'; }
    return '';
  }

  downloadReport(dl: { filename: string; content?: string; base64?: string; mime?: string; encoding?: string }) {
    try {
      let blob: Blob;
      // Binary-safe path: backend sends base64 + mime for PDF/XLSX (and CSV).
      // Decode it so binary files aren't corrupted by a utf-8 text Blob.
      if (dl.encoding === 'base64' && dl.base64) {
        const bin = atob(dl.base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) { bytes[i] = bin.charCodeAt(i); }
        blob = new Blob([bytes], { type: dl.mime || 'application/octet-stream' });
      } else {
        blob = new Blob([dl.content || ''], { type: dl.mime || 'text/plain;charset=utf-8' });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = dl.filename || 'Arya-report.txt';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {}
  }

  async send(message: string, silent = false, displayText?: string) {
    if (!message || this.inFlight || !this.available) { return; }

    // A new turn snaps any still-animating previous answer to its full text so
    // the prior reveal doesn't keep typing behind the new message.
    this.flushAllTypewriters();

    // Scope correction: if the user re-scoped to a specific campaign via the
    // scope chip selector, embed the campaign in the message text so the
    // (stateless, text-scoped) backend resolves to it. The visible bubble keeps
    // the user's original wording. Sticky until they clear the chip (×).
    let outgoing = message;
    if (!silent && this.scopeOverrideProjectName) {
      if (!displayText) { displayText = message; }
      outgoing = `For campaign "${this.scopeOverrideProjectName}": ${message}`;
    }

    // Route through streaming if fetch + ReadableStream are available; fall
    // back to the blocking path otherwise (very old browsers, or if the stream
    // fetch fails before any event arrives).
    const canStream = typeof fetch === 'function' && typeof ReadableStream !== 'undefined';
    if (canStream) {
      return this.sendStreaming(outgoing, silent, displayText);
    }
    return this.sendBlocking(outgoing, silent, displayText);
  }

  /**
   * Streaming path — POST /v1/assistant/chat/stream and consume SSE frames.
   *
   * Events:
   *   status  → update live typing-status note on the pending bot bubble
   *   token   → append incremental text chunks (live typing effect)
   *   done    → finalize with authoritative body (same shape as /chat response)
   *   error   → show backend error message gracefully
   *
   * Falls back to sendBlocking() if the fetch fails / returns non-OK BEFORE any
   * SSE event is received (network error, proxy 5xx, etc.) so nothing regresses.
   */
  private async sendStreaming(message: string, silent = false, displayText?: string): Promise<void> {
    this.inFlight = true;
    this.stoppedNote = '';
    this.startTypingStatus(message);
    this.quickReplies = [];
    this.showPicker = false;

    if (!silent) {
      this.pushMsg(displayText || message, 'me');
      this.addHistory('user', message);
    }

    // When true the fallback path has already taken ownership of cleanup; the
    // streaming finally block must be a no-op to avoid double-focus/scroll.
    let delegatedToFallback = false;

    const url = `${environment.assistantApiUrl}/v1/assistant/chat/stream`;
    const jwt = localStorage.getItem('jwtToken') || '';
    const token = jwt.replace(/^bearer\s+/i, '').trim();
    // Resolve entity_id the same way AssistantService does it internally:
    // read h4u-current-user from localStorage (the service's private method
    // cannot be called here without an any-cast; replicating is cleaner).
    let entityId = '';
    try {
      const cu = JSON.parse(localStorage.getItem('h4u-current-user') || 'null');
      entityId = (cu && cu.entity && cu.entity.id) || '';
    } catch (_) {}
    // Generate the request_id client-side and send it up front, so the Stop
    // button can cancel the backend's background processing MID-FLIGHT (before
    // the final response arrives — which is the whole point for ~400s queries).
    const reqId = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `req_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    this.inflightRequestId = reqId;

    const bodyObj = {
      entity_id: entityId,
      message,
      greeted: this.greeted,
      owner_name: this.ownerName || undefined,
      request_id: reqId,
    };

    // Placeholder bot bubble that will be updated in-place as SSE events arrive.
    const botMsg: ChatMsg = { text: '', who: 'bot', streaming: true, ts: Date.now() };

    let streamStarted = false;    // true once we've received at least one SSE event
    let accTokens = '';           // accumulated token chunks
    let botShown = false;         // whether botMsg has been pushed into the feed

    // Show the bot bubble LAZILY — only once real content (a token) or the final
    // answer arrives. During the status/heartbeat phase we show just the typing
    // indicator (dots + status note), never an empty bubble. streamingShown also
    // hides the separate typing indicator so the two never appear together.
    const ensureShown = () => {
      if (!botShown) {
        this.messages.push(botMsg);
        botShown = true;
        this.streamingShown = true;
        this.shouldScrollFeed = true;
      }
    };

    const finalise = (r: AssistantReply) => {
      ensureShown();
      const text = r.assistant_message || accTokens || '(no reply)';
      botMsg.text = text;
      botMsg.mode = r.mode;
      botMsg.download = r.download;
      botMsg.msgType = this.deriveMsgType(r);
      // If the backend streamed real token chunks, the bubble already animated
      // in progressively — finalise it instantly to the authoritative text.
      // If NO tokens streamed (deterministic/data answers arrive in one `done`
      // frame), animate the final text with the client-side typewriter so it
      // never "flips in" all at once (GPT/Claude-like reveal). XSS-safe: the
      // typewriter renders through the same escape-first markdownToHtml path.
      if (accTokens) {
        botMsg.streaming = false;
        botMsg.displayText = undefined;
        botMsg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(text));
      } else {
        this.typewriterReveal(botMsg, text);
      }
      // Capture scope chip + request_id from backend response.
      if (r.scope) {
        botMsg.scope = r.scope;
        this.lastScope = r.scope;
      }
      if (r.request_id) {
        botMsg.requestId = r.request_id;
        this.inflightRequestId = r.request_id;
      }
      this.addHistory('assistant', text);

      if (r.mode === 'select' && r.project_id) {
        this.setSelectedProject(r.project_id, r.project_name || '');
      }
      if (r.mode === 'pick') {
        this.showPicker = true;
        this.pickerCampaigns = this.normaliseCampaigns(r.quick_replies || []);
      } else {
        this.quickReplies = r.quick_replies || [];
      }
      this.shouldScrollFeed = true;
    };

    try {
      let res: Response;
      try {
        res = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bodyObj),
        });
      } catch (_) {
        // Network-level failure before any event → fall back.
        delegatedToFallback = true;
        await this.sendBlocking(message, true /* already pushed user bubble */);
        return;
      }

      if (!res.ok) {
        // HTTP error before any event → check auth, then fall back.
        if (res.status === 401 || res.status === 403) {
          this.available = false;
          const errMsg: ChatMsg = {
            text: 'Your session has expired — please refresh the page to continue.',
            who: 'bot', ts: Date.now(),
          };
          this.messages.push(errMsg);
          this.typewriterReveal(errMsg, errMsg.text);
          return;
        }
        delegatedToFallback = true;
        await this.sendBlocking(message, true);
        return;
      }

      // Stream opened. Do NOT push the bubble yet — wait for the first token (or
      // the final answer). Until then only the typing indicator (dots + status
      // note) shows, so there is no empty bubble next to the dots.
      const reader = res.body!.getReader();
      this.inflightReader = reader;
      const decoder = new TextDecoder();
      let buf = '';

      const processFrame = (frame: string) => {
        // A single SSE frame: "event: <name>\ndata: <json>"
        const lines = frame.split('\n');
        let eventName = '';
        let dataStr = '';
        for (const line of lines) {
          if (line.startsWith('event:')) { eventName = line.slice(6).trim(); }
          else if (line.startsWith('data:')) { dataStr = line.slice(5).trim(); }
        }
        if (!eventName || !dataStr) { return; }

        streamStarted = true;

        let parsed: any;
        try { parsed = JSON.parse(dataStr); } catch (_) { return; }

        if (eventName === 'status') {
          // Update the live typing-status note beside the spinner
          const note = (parsed && parsed.note) || 'Working…';
          this.typingStatus = note;
          // Keep the bubble visible with a status hint (no text yet)
          if (!accTokens) {
            botMsg.displayText = '';
            botMsg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml('');
          }
          this.shouldScrollFeed = true;

        } else if (eventName === 'token') {
          // First token → reveal the bubble (this also hides the typing indicator).
          ensureShown();
          // Incremental text chunk — append and render immediately
          accTokens += (parsed && parsed.t) || '';
          botMsg.text = accTokens;
          botMsg.displayText = accTokens;
          botMsg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(accTokens));
          this.shouldScrollFeed = true;

        } else if (eventName === 'done') {
          // Authoritative final body — finalize the bubble
          finalise(parsed as AssistantReply);

        } else if (eventName === 'error') {
          ensureShown();
          const errText = (parsed && parsed.message) || '⚠️ Could not reach the assistant. Please try again.';
          botMsg.text = errText;
          botMsg.streaming = false;
          botMsg.displayText = undefined;
          botMsg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(errText));
          this.addHistory('assistant', errText);
          this.shouldScrollFeed = true;
        }
      };

      // Read the stream until done
      while (true) {
        let done: boolean;
        let value: Uint8Array | undefined;
        try {
          ({ done, value } = await reader.read());
        } catch (_) {
          // Stream read error after receiving some events — treat as done
          break;
        }
        if (done) { break; }
        buf += decoder.decode(value, { stream: true });

        // SSE frames are separated by double newlines
        let boundary = buf.indexOf('\n\n');
        while (boundary !== -1) {
          const frame = buf.slice(0, boundary).trim();
          buf = buf.slice(boundary + 2);
          if (frame) { processFrame(frame); }
          boundary = buf.indexOf('\n\n');
        }
      }

      // If we got no events at all (edge proxy swallowed the stream), fall back.
      if (!streamStarted) {
        this.messages = this.messages.filter(m => m !== botMsg);
        delegatedToFallback = true;
        await this.sendBlocking(message, true);
        return;
      }

      // If stream ended without a done/error event (truncated), mark complete
      // using whatever tokens arrived.
      if (botMsg.streaming) {
        ensureShown();
        const text = accTokens || '(no reply)';
        botMsg.text = text;
        botMsg.streaming = false;
        botMsg.displayText = undefined;
        botMsg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(text));
        this.addHistory('assistant', text);
        this.shouldScrollFeed = true;
      }

    } catch (e) {
      if (!streamStarted) {
        // Nothing shown yet — remove placeholder if present and fall back
        this.messages = this.messages.filter(m => m !== botMsg);
        delegatedToFallback = true;
        await this.sendBlocking(message, true);
        return;
      }
      // Stream broke after showing something — mark the bubble as ended
      if (botMsg.streaming) {
        ensureShown();
        const text = accTokens || '⚠️ Could not reach the assistant. Please try again.';
        botMsg.text = text;
        botMsg.streaming = false;
        botMsg.displayText = undefined;
        botMsg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(markdownToHtml(text));
        this.shouldScrollFeed = true;
      }
    } finally {
      // When the fallback has taken ownership of cleanup, sendBlocking's own
      // finally already ran — skip to avoid double inFlight=false / double focus.
      if (!delegatedToFallback) {
        this.inflightReader = null;
        this.inflightRequestId = null;
        this.inFlight = false;
        this.streamingShown = false;
        this.stopping = false;
        this.stopTypingStatus();
        this.shouldScrollFeed = true;
        setTimeout(() => {
          if (this.chatInputEl && this.chatInputEl.nativeElement) {
            this.chatInputEl.nativeElement.focus();
          }
        }, 0);
      }
    }
  }

  /**
   * Blocking (non-streaming) fallback — the original send() logic unchanged.
   * Used when fetch/ReadableStream is unavailable, or when the streaming fetch
   * fails before any SSE event arrives.
   *
   * `alreadyPushedUserBubble`: pass true when sendStreaming already pushed the
   * user message so we don't duplicate it.
   */
  private async sendBlocking(message: string, alreadyPushedUserBubble = false, displayText?: string): Promise<void> {
    // Guard: inFlight is set by the caller (send/sendStreaming) before routing here.
    // Only set it ourselves if we are being called as a direct fallback from a path
    // that didn't set it (shouldn't happen with current routing, but be safe).
    if (!this.inFlight) {
      this.inFlight = true;
      this.startTypingStatus(message);
      this.quickReplies = [];
      this.showPicker = false;
    }

    if (!alreadyPushedUserBubble) {
      this.pushMsg(displayText || message, 'me');
      this.addHistory('user', message);
    }

    const t0 = performance.now();

    try {
      // STATELESS REQUESTS (2026-06-22): do NOT send client-side projectId or
      // conversation history. A sticky projectId mis-scoped account-wide queries,
      // and the in-memory history polluted the LLM prompt — both caused the UI to
      // give worse/inconsistent answers than a clean direct call. Each turn is now
      // independent and deterministic (the backend resolves project scope from the
      // message text). Proper per-user memory/context will be built server-side.
      const r: AssistantReply = await this.assistant.chat(message, {
        greeted: this.greeted,
        ownerName: this.ownerName || undefined,
      });

      // 450 ms minimum so the typing indicator doesn't flicker away instantly
      const hold = Math.max(0, 450 - (performance.now() - t0));
      await new Promise<void>(res => setTimeout(res, hold));

      const text = r.assistant_message || '(no reply)';
      this.addHistory('assistant', text);

      if (r.mode === 'select' && r.project_id) {
        this.setSelectedProject(r.project_id, r.project_name || '');
      }

      const botMsg: ChatMsg = { text, who: 'bot', mode: r.mode, download: r.download, msgType: this.deriveMsgType(r), ts: Date.now() };
      if (r.scope) { botMsg.scope = r.scope; this.lastScope = r.scope; }
      if (r.request_id) { botMsg.requestId = r.request_id; }
      this.messages.push(botMsg);
      this.typewriterReveal(botMsg, text);

      if (r.mode === 'pick') {
        this.showPicker = true;
        this.pickerCampaigns = this.normaliseCampaigns(r.quick_replies || []);
      } else {
        this.quickReplies = r.quick_replies || [];
      }
    } catch (e) {
      const status = (e as any) && (e as any).status;
      if (status === 401 || status === 403) {
        this.available = false;
        const errMsg: ChatMsg = {
          text: 'Your session has expired — please refresh the page to continue.',
          who: 'bot',
          ts: Date.now(),
        };
        this.messages.push(errMsg);
        this.typewriterReveal(errMsg, errMsg.text);
      } else {
        const errMsg: ChatMsg = { text: '⚠️ Could not reach the assistant. Please try again.', who: 'bot', ts: Date.now() };
        this.messages.push(errMsg);
        this.typewriterReveal(errMsg, errMsg.text);
      }
    } finally {
      this.inFlight = false;
      this.stopTypingStatus();
      this.shouldScrollFeed = true;
      // Keep focus in the input so the user can type their next message
      // without having to click the box again.
      setTimeout(() => {
        if (this.chatInputEl && this.chatInputEl.nativeElement) {
          this.chatInputEl.nativeElement.focus();
        }
      }, 0);
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  /**
   * First status word reflects what the user actually asked (not a canned
   * rotation). NOTE: /chat is a single blocking call with no streamed stages,
   * so we can't show true per-tool progress; the label maps to request intent
   * and then to honest elapsed state (see startTypingStatus).
   */
  private statusForMessage(message: string): string {
    const m = (message || '').toLowerCase();
    if (/\b(report|download|export|pdf|excel|csv|xlsx)\b/.test(m)) { return 'Preparing your report…'; }
    if (/\b(call|calls|talk\s*time|duration|minutes)\b/.test(m)) { return 'Pulling your call data…'; }
    if (/\b(lead|leads|connected|outcome|outcomes|positive|qualified)\b/.test(m)) { return 'Fetching your leads…'; }
    if (/\b(credit|credits|usage|balance)\b/.test(m)) { return 'Checking your credits…'; }
    if (/\b(campaign|campaigns|project|projects)\b/.test(m)) { return 'Looking up your campaigns…'; }
    if (/\b(whatsapp|channel|notification|notifications|report)\b/.test(m)) { return 'Checking WhatsApp…'; }
    return 'Thinking…';
  }

  private startTypingStatus(message?: string) {
    this.stopTypingStatus();
    // Intent-derived first label, then honest elapsed-state progression — no
    // fabricated "reading → crunching → pulling" steps that may never happen.
    const seq = [this.statusForMessage(message || ''), 'Working on it…', 'Still working — almost there…'];
    let i = 0;
    this.typingStatus = seq[0];
    this.typingTimer = setInterval(() => {
      i++;
      if (i >= seq.length) { this.stopTypingStatus(); return; }
      this.typingStatus = seq[i];
    }, 2600);
  }

  private stopTypingStatus() {
    if (this.typingTimer) { clearInterval(this.typingTimer); this.typingTimer = undefined; }
  }

  private pushMsg(text: string, who: 'me' | 'bot') {
    this.messages.push({ text, who, ts: Date.now() });
    this.shouldScrollFeed = true;
  }

  private addHistory(role: 'user' | 'assistant', text: string) {
    this.history.push({ role, text });
    if (this.history.length > HISTORY_MAX) {
      this.history = this.history.slice(-HISTORY_MAX);
    }
  }

  private normaliseCampaigns(arr: { id: string; label: string }[]): Campaign[] {
    return arr.map(c => ({
      id: c.id.startsWith('project:') ? c.id.slice(8) : c.id,
      label: c.label,
    }));
  }
}
